import { getFlowDestination } from "containers/App/AppAuth";
import { isRedirectURLAllowed, RedirectNotAllowedError } from "utils/redirect";

/**
 * @name getReturnRedirectData
 * @param {SessionCTX} sessionCtx
 * @param {string} [statusStr] - optional statusStr, like '{flow}.{status}.{extra data if needed}'
 * @param {Array<string>} [allowedDomainRedirectPatterns] - integrator allowed redirect patterns used to validate the external redirect domain
 * @returns {}
 * @description Used in the case that we have to redirect the user to some 3 party for OAuth purposes (Paypal, Plaid, etc).
 * This will examing the sessionCtx, if 'intCtx' has a value we know we are coming from a publisher's page using the widget script.
 * At that point, we can check if there is an override URL to use (which would be used in the case of a native mobile app, which
 * would have to direct out of the app to a browser, then have a way to return to the native application through something like
 * custom URL scheme)
 * The external redirect target (the supplied postOAuthURLOverride, or otherwise the wrapping page's intCtx) is validated
 * against the integrator's allowed_domain_redirect_patterns; a disallowed origin throws RedirectNotAllowedError, which the
 * GlobalErrorBoundary surfaces as a user-facing "Invalid redirect domain" message. The 'flow' branch below resolves
 * to a static internal path (getFlowDestination/flowMap), so it is not validated.
*/
const getReturnRedirectData = (sessionCtx, statusStr, allowedDomainRedirectPatterns) => {
  const intCtx = sessionCtx?.intCtx;
  const flow = sessionCtx?.flow;
  const postOAuthURLOverride = sessionCtx?.flowConfig?.postOAuthURLOverride;
  let url;
  let useReactRedirect;
  if (intCtx) {
    url = postOAuthURLOverride || intCtx;
    if (statusStr) {
      // need to add statusStr as a query param, so the widget can set sessionCtx.flowState itself
      const u = new URL(url);
      u.searchParams.set("tilia-state", statusStr);
      url = u.href;
    }
    useReactRedirect = false;
    // Validate the external redirect origin against integrator allowed patterns.
    if (!isRedirectURLAllowed(url, { allowed_domain_redirect_patterns: allowedDomainRedirectPatterns })) {
      throw new RedirectNotAllowedError();
    }
  } else if (flow) {
    // flow should be setting sessionCtx.flowState itself, so we don't have to handle statusStr here
    url = getFlowDestination(flow);
    useReactRedirect = true;
  }

  return { url, useReactRedirect };
};

export default getReturnRedirectData;
